/**
 * Catalog proofs, frozen versions, the comparison with today's records, and the read-only catalog a
 * buyer sees from a shared link. The same view prints.
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { formatDate, formatMoney, formatNumber } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { useApi, useQuery } from "../../../web/lib/query";
import { api } from "../../../web/lib/api";
import { Page } from "../../../web/shell/Layout";
import { Button, Card, Chip, Disclosure, Empty, ErrorNote, Icon, List, Loading, PageHeader, Row, Section } from "../../../web/ui/kit";
import type { CatalogAnimal, CatalogChange, CatalogSnapshot, CatalogSummary, CatalogVersionDTO, OfferingDTO, PublicCatalogDTO } from "../shared/types";
import { act, copyLink, kindLabel, LotNumber, lotKindLabel, post, priceText } from "./common";
import { CHANGE_FIELD_LABELS, PEDIGREE_LABELS, WEIGHT_LABELS } from "../shared/labels";
import { useMe } from "../../../web/lib/session";


function changeValue(field: string, v: string | number | null, currency: string): string {
  if (v === null || v === undefined || v === "") return t("nothing");
  if (field === "asking" && typeof v === "number") return formatMoney({ minor: v, currency });
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return formatDate(v);
  if (typeof v === "number") return formatNumber(v, 2);
  return String(v);
}

export function ChangeList({ changes }: { changes: CatalogChange[] }) {
  const { data: me } = useMe();
  const currency = me?.ranch?.currency ?? "USD";
  if (!changes.length) return <p className="muted">{t("Nothing changed.")}</p>;
  return (
    <ul className="sales-changes">
      {changes.map((c, i) => {
        const label = c.field.startsWith("epd.") ? t("EPD {{trait}}", { trait: c.field.slice(4) }) : t(CHANGE_FIELD_LABELS[c.field] ?? c.field);
        const where = [c.lot ? t("Lot {{lot}}", { lot: c.lot }) : null, c.animal].filter(Boolean).join(" · ");
        return (
          <li key={i}>
            <span className="sales-change-where">{where}</span>
            {c.change === "added" ? <span>{t("Added: {{what}}", { what: c.field === "animal" || c.field === "lot" ? changeValue(c.field, c.after, currency) : label })}</span>
              : c.change === "removed" ? <span>{t("Removed: {{what}}", { what: c.field === "animal" || c.field === "lot" ? changeValue(c.field, c.before, currency) : label })}</span>
              : <span>{label}: <s>{changeValue(c.field, c.before, currency)}</s> → <strong>{changeValue(c.field, c.after, currency)}</strong></span>}
          </li>
        );
      })}
    </ul>
  );
}

export function CatalogTab({ o }: { o: OfferingDTO }) {
  const approved = o.catalogs.find((c) => c.status === "approved") ?? null;
  const proof = o.catalogs.find((c) => c.status === "proof") ?? null;
  const older = o.catalogs.filter((c) => c.status === "superseded");
  const compare = useApi<{ approved: { id: string; version: number } | null; changes: CatalogChange[] }>(approved ? `/m/sales/offerings/${o.job.id}/catalog/compare` : null);
  const [busy, setBusy] = useState(false);
  const build = async () => { setBusy(true); await act(() => post(`/offerings/${o.job.id}/catalog`, {}), t("Proof built")); setBusy(false); };
  if (!o.catalogs.length) {
    return <Empty icon="catalog" title={t("No catalog yet")} body={t("Hondo builds a proof from the lots and records: pedigree, EPDs with their dates, weights, photos, footnotes and retained interests. Approving it freezes that version.")}
      action={o.can.manage ? <Button variant="primary" size="lg" icon="catalog" busy={busy} disabled={!o.lots.length} onClick={() => void build()}>{t("Build a proof")}</Button> : undefined} />;
  }
  return (
    <div className="stack">
      {proof && (
        <Card className="sales-next">
          <span className="eyebrow">{t("Proof, version {{version}}", { version: proof.version })}</span>
          <p className="sales-next-title">{approved ? t("Revision waiting for approval") : t("Check the proof, then approve it")}</p>
          {approved && <Disclosure label={t("What changes from version {{version}}", { version: approved.version })} count={proof.changes.length} defaultOpen><ChangeList changes={proof.changes} /></Disclosure>}
          <div className="row-actions">
            <Button to={`/work/sales/catalogs/${proof.id}`} icon="eye">{t("Look it over")}</Button>
            {o.can.manage && <Button variant="primary" icon="check" onClick={() => void act(() => post(`/catalogs/${proof.id}/approve`), t("Approved and frozen"))}>{t("Approve and freeze")}</Button>}
            {o.can.manage && <Button variant="ghost" icon="reset" busy={busy} onClick={() => void build()}>{t("Rebuild from today's records")}</Button>}
          </div>
        </Card>
      )}
      {approved && <ApprovedCard c={approved} o={o} changes={compare.data?.changes ?? []} building={busy} onRevise={() => void build()} hasProof={!!proof} />}
      {older.length > 0 && (
        <Disclosure label={t("Earlier versions")} count={older.length}>
          <List>{older.map((c) => <Row key={c.id} icon="catalog" to={`/work/sales/catalogs/${c.id}`} title={t("Version {{version}}", { version: c.version })} meta={c.approved_at ? t("Approved {{date}}", { date: formatDate(c.approved_at) }) : formatDate(c.created_at)} />)}</List>
        </Disclosure>
      )}
    </div>
  );
}

function ApprovedCard({ c, o, changes, building, onRevise, hasProof }: { c: CatalogSummary; o: OfferingDTO; changes: CatalogChange[]; building: boolean; onRevise: () => void; hasProof: boolean }) {
  return (
    <Section title={t("Approved, version {{version}}", { version: c.version })} action={<Chip tone="good" icon="done">{t("Frozen")}</Chip>}>
      <Card>
        <p className="muted">{[c.approved_at ? t("Approved {{date}}", { date: formatDate(c.approved_at) }) : null, c.approved_by].filter(Boolean).join(" · ")}</p>
        <div className="row-actions">
          <Button to={`/work/sales/catalogs/${c.id}`} icon="eye">{t("Open")}</Button>
          <Button to={`/work/sales/catalogs/${c.id}?print=1`} icon="print">{t("Print")}</Button>
          <a className="btn btn-secondary btn-md" href={`/api/m/sales/catalogs/${c.id}/csv`} download><Icon name="download" size={18} /><span>{t("Spreadsheet for the sale manager")}</span></a>
          {o.can.manage && (c.share_token
            ? <><Button icon="link" onClick={() => void copyLink(`/catalog/${c.share_token}`)}>{t("Copy buyer link")}</Button><Button variant="ghost" onClick={() => void act(() => post(`/catalogs/${c.id}/unshare`), t("Link turned off"))}>{t("Turn off link")}</Button></>
            : <Button icon="share" onClick={() => void act(async () => { const r = await post<{ token: string }>(`/catalogs/${c.id}/share`); await copyLink(`/catalog/${r.token}`); }, t("Buyer link ready"))}>{t("Share with buyers")}</Button>)}
        </div>
        {c.changes.length > 0 && <Disclosure label={t("Changed from the version before")} count={c.changes.length}><ChangeList changes={c.changes} /></Disclosure>}
      </Card>
      {changes.length > 0 && !hasProof && (
        <Card className="sales-next">
          <p className="sales-next-title">{plural(changes.length, "{{count}} thing in the records changed since this version", "{{count}} things in the records changed since this version")}</p>
          <ChangeList changes={changes.slice(0, 12)} />
          {o.can.manage && <Button variant="primary" icon="catalog" busy={building} onClick={onRevise}>{t("Make a revised proof")}</Button>}
        </Card>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- the catalog itself

const PEDIGREE = Object.entries(PEDIGREE_LABELS) as Array<[keyof CatalogAnimal["pedigree"], string]>;
const WEIGHTS = Object.entries(WEIGHT_LABELS) as Array<[keyof CatalogAnimal["weights"], string]>;

function AnimalBlock({ a, photo }: { a: CatalogAnimal; photo: (id: string) => string }) {
  const sexLabel = t(a.sex === "male" ? "Male" : a.sex === "female" ? "Female" : "Unknown sex");
  return (
    <div className="sales-cat-animal">
      {a.photos[0] && <img className="sales-cat-photo" src={photo(a.photos[0].id)} alt={t("Photo of {{animal}}", { animal: [a.tag, a.name].filter(Boolean).join(" ") })} loading="lazy" />}
      <div className="sales-cat-id">
        <strong className="mono">{a.tag ?? ""}</strong>{a.name && <span className="sales-cat-name">{a.name}</span>}
        <span className="muted">{[sexLabel, a.birth_date ? t("born {{date}}", { date: formatDate(a.birth_date) }) : null, a.breed, a.registration ? `${a.registration.association ?? t("Reg.")} ${a.registration.number}` : null].filter(Boolean).join(" · ")}</span>
      </div>
      {Object.keys(a.pedigree).length > 0 && (
        <dl className="sales-cat-pedigree">
          {PEDIGREE.filter(([k]) => a.pedigree[k]).map(([k, label]) => <div key={k} className={`ped-${k}`}><dt>{t(label)}</dt><dd>{a.pedigree[k]}</dd></div>)}
        </dl>
      )}
      {a.epds && (
        <div className="sales-cat-table">
          <table>
            <thead><tr>{Object.keys(a.epds.values).map((k) => <th key={k} scope="col">{k}</th>)}</tr></thead>
            <tbody><tr>{Object.values(a.epds.values).map((v, i) => <td key={i}>{formatNumber(v, 2)}</td>)}</tr></tbody>
          </table>
          <p className="sales-cat-asof">{a.epds.as_of ? t("EPDs as of {{date}}", { date: formatDate(a.epds.as_of) }) : t("EPD date not recorded")}</p>
        </div>
      )}
      {WEIGHTS.some(([k]) => a.weights[k]) && (
        <dl className="sales-cat-weights">
          {WEIGHTS.filter(([k]) => a.weights[k]).map(([k, label]) => {
            const w = a.weights[k]!;
            return <div key={k}><dt>{t(label)}</dt><dd>{formatNumber(w.value, 0)} {w.unit}{w.adjusted ? ` (${t("adj. {{value}}", { value: formatNumber(w.adjusted, 0) })})` : ""}{w.on && <span className="muted"> · {formatDate(w.on)}</span>}</dd></div>;
          })}
          {a.adg !== null && <div><dt>{t("ADG")}</dt><dd>{formatNumber(a.adg, 2)}</dd></div>}
        </dl>
      )}
    </div>
  );
}

export function CatalogView({ snapshot, version, approvedAt, changes, photo }: { snapshot: CatalogSnapshot; version: number; approvedAt: string | null; changes?: CatalogChange[]; photo: (id: string) => string }) {
  const s = snapshot;
  return (
    <article className="sales-catalog">
      <header className="sales-cat-head">
        <p className="eyebrow">{s.offering.ranch}</p>
        <h1>{s.offering.title}</h1>
        <p className="sales-cat-sub">{[kindLabel(s.offering.kind), s.offering.sale_date ? formatDate(s.offering.sale_date, "date", { weekday: true }) : null, s.offering.location, s.offering.manager].filter(Boolean).join(" · ")}</p>
        <p className="sales-cat-version">{approvedAt ? t("Catalog version {{version}}, approved {{date}}", { version, date: formatDate(approvedAt) }) : t("Proof, version {{version}}: not approved yet", { version })}</p>
      </header>
      {changes && changes.length > 0 && <section className="sales-cat-changes"><h2>{t("Changes since the last version")}</h2><ChangeList changes={changes} /></section>}
      {s.offering.terms && <section className="sales-cat-terms"><h2>{t("Terms")}</h2><p className="sales-prose">{s.offering.terms}</p></section>}
      {s.lots.map((l) => (
        <section key={l.lot_id} className="sales-cat-lot">
          <div className="sales-cat-lot-head">
            <LotNumber n={l.lot_number} size="lg" />
            <div>
              <h2>{l.title ?? (l.animals.length ? l.animals.map((a) => [a.tag, a.name].filter(Boolean).join(" ")).join(", ") : plural(l.head_count ?? 0, "{{count}} head", "{{count}} head"))}</h2>
              <p className="muted">{[l.kind !== "animal" ? lotKindLabel(l.kind) : null, l.head_count && l.animals.length === 0 ? plural(l.head_count, "{{count}} head", "{{count}} head") : null, l.asking ? priceText(l.asking, l.price_basis) : null].filter(Boolean).join(" · ")}</p>
            </div>
          </div>
          {l.animals.map((a) => <AnimalBlock key={a.key} a={a} photo={photo} />)}
          {l.footnote && <p className="sales-cat-footnote">{l.footnote}</p>}
          {l.retained && <p className="sales-cat-retained"><Icon name="info" size={14} />{t("Seller keeps: {{what}}", { what: l.retained })}</p>}
        </section>
      ))}
      <footer className="sales-cat-foot muted">{t("Prepared with Hondo. Figures are as recorded on the dates shown.")}</footer>
    </article>
  );
}

/** One version, from inside the app: look it over or print it. */
export function CatalogPage() {
  useLanguage();
  const { catalogId } = useParams();
  const [params] = useSearchParams();
  const q = useApi<CatalogVersionDTO>(`/m/sales/catalogs/${catalogId}`);
  const ready = !!q.data;
  useEffect(() => { if (ready && params.get("print") === "1") setTimeout(() => window.print(), 300); }, [ready, params]);
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const c = q.data;
  return (
    <Page>
      <div className="sales-print-hide">
        <PageHeader back={{ to: `/work/jobs/${c.offering.id}?tab=catalog`, label: c.offering.label }} title={t("Version {{version}}", { version: c.version })}
          subtitle={c.status === "approved" ? t("Approved and frozen") : c.status === "superseded" ? t("Replaced by a newer version") : t("Proof, not approved yet")}
          actions={<Button icon="print" onClick={() => window.print()}>{t("Print")}</Button>} />
      </div>
      <CatalogView snapshot={c.snapshot} version={c.version} approvedAt={c.approved_at} changes={c.changes} photo={(id) => `/api/m/sales/photos/${id}`} />
    </Page>
  );
}

/** What a buyer sees from a shared link: read-only, the approved version only, with its changes. */
export function PublicCatalog({ token: given }: { token?: string }) {
  useLanguage();
  const params = useParams();
  const token = given ?? params.token ?? "";
  const q = useQuery<PublicCatalogDTO>({ queryKey: ["public-catalog", token], queryFn: () => api.get<PublicCatalogDTO>(`/m/sales/public/catalog/${token}`), retry: false });
  if (q.isLoading) return <Loading />;
  if (!q.data) return <div className="page"><Empty icon="catalog" title={t("This catalog link doesn't work anymore.")} body={t("Ask the seller for a new link.")} /></div>;
  return (
    <div className="page sales-public">
      <div className="sales-print-hide sales-public-bar"><Button icon="print" onClick={() => window.print()}>{t("Print")}</Button></div>
      <CatalogView snapshot={q.data.snapshot} version={q.data.version} approvedAt={q.data.approved_at} changes={q.data.changes} photo={(id) => `/api/m/sales/public/catalog/${token}/photos/${id}`} />
    </div>
  );
}

